import React, { useEffect, useState, useRef } from "react";
import PageLayout from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  Calendar,
  Users,
  Award,
  ArrowRight,
  Linkedin,
  Clock,
} from "lucide-react";
import Swal from "sweetalert2";
import confetti from "canvas-confetti";
import { Helmet } from "react-helmet";

// Contest configuration
const CONTEST_CONFIG = {
  startDate: new Date("2025-04-01"),
  endDate: new Date("2022-06-14"),
  maxParticipants: 500,
  // Original Google Script URL (will be used in production)
  googleScriptUrl:
    "https://script.google.com/macros/s/AKfycbyvREpKtpV-jeiwhznxzAzkavWCcDe6tDwR9_B-JSJlh70r5-qeQ0rikAYYz2quBiPiMw/exec",
  // Local proxy URL for development
  // proxyUrl: "https://gappscript.smitthwillson1213.workers.dev",
  refreshInterval: 60000,
  useMockData: false,
  // Determine if we're in development mode
//   isDevelopment:
//     window.location.hostname === "localhost" ||
//     window.location.hostname === "127.0.0.1",
 };

interface ContestStats {
  totalParticipants: number;
  highRatings: number;
  recentParticipants: Array<{
    name: string;
    rating: number;
    date: string;
  }>;
}

interface FormData {
  name: string;
  linkedin: string;
  rating: number;
  comment: string;
}

const Services = () => {
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);

  const section4Ref = useRef(null);
  const section5Ref = useRef(null);
  const section6Ref = useRef(null);
  const section7Ref = useRef(null);

  // Animation Trigger
  const useAnimateOnScroll = (refs, animationClass) => {
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animate__animated", animationClass);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.3 }
      );

      refs.forEach((ref) => {
        if (ref.current) observer.observe(ref.current);
      });

      return () => observer.disconnect();
    }, [refs, animationClass]);
  };

  useAnimateOnScroll(
    [section3Ref, section2Ref, section1Ref],
    "animate__rollIn"
  );
  useAnimateOnScroll(
    [section4Ref, section5Ref, section6Ref],
    "animate__rollIn"
  );
  useAnimateOnScroll([section7Ref], "animate__slideInRight");

  const [formData, setFormData] = useState<FormData>({
    name: "",
    linkedin: "",
    rating: 0,
    comment: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contestStats, setContestStats] = useState<ContestStats>({
    totalParticipants: 0,
    highRatings: 0,
    recentParticipants: [],
  });
  const [isContestActive, setIsContestActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const confettiRef = useRef<HTMLDivElement>(null);

  // Check if contest is still active
  useEffect(() => {
    const now = new Date();
    const isBeforeEnd = now < CONTEST_CONFIG.endDate;
    const isAfterStart = now > CONTEST_CONFIG.startDate;
    const isUnderMaxParticipants =
      contestStats.totalParticipants < CONTEST_CONFIG.maxParticipants;

    setIsContestActive(isBeforeEnd && isAfterStart && isUnderMaxParticipants);

    // Calculate time left
    if (isBeforeEnd) {
      const updateTimeLeft = () => {
        const now = new Date();
        const diff = CONTEST_CONFIG.endDate.getTime() - now.getTime();

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        setTimeLeft(`${days}j ${hours}h ${minutes}m`);
      };

      updateTimeLeft();
      const timer = setInterval(updateTimeLeft, 60000);
      return () => clearInterval(timer);
    } else {
      setTimeLeft("Terminé");
    }
  }, [contestStats.totalParticipants]);

  // Fetch contest stats
  const fetchContestStats = async () => {
    try {
      let statsData: ContestStats;

      if (CONTEST_CONFIG.useMockData) {
        // Mock data for development or when API is unavailable
        console.log("Using mock data for contest stats");
        statsData = {
          totalParticipants: Math.floor(Math.random() * 500) + 100,
          highRatings: Math.floor(Math.random() * 300) + 50,
          recentParticipants: [],
        };
      } else {
        // Try to fetch from the Google Script with proper error handling
        console.log("Fetching contest stats from API");
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

        // Determine which URL to use based on environment
        const apiUrl = `${CONTEST_CONFIG.googleScriptUrl}?action=getStats`;

        console.log(`Using API URL: ${apiUrl}`);

        const response = await fetch(apiUrl, {
          method: "GET",
          signal: controller.signal,
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
          },
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        statsData = await response.json();
        console.log("Stats data received:", statsData);
      }

      setContestStats(statsData);
    } catch (error) {
      console.error("Error fetching contest stats:", error);
      // Fallback to mock data on error
      setContestStats({
        totalParticipants: 0,
        highRatings: 0,
        recentParticipants: [],
      });
    }
  };

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle star rating
  const handleRatingChange = (rating: number) => {
    setFormData((prev) => ({ ...prev, rating }));
  };

  // Format LinkedIn URL
  const formatLinkedIn = (url: string) => {
    if (!url) return "";
    if (url.startsWith("https://www.linkedin.com/")) return url;
    if (url.startsWith("www.linkedin.com/")) return `https://${url}`;
    if (url.startsWith("linkedin.com/")) return `https://www.${url}`;
    return `https://www.linkedin.com/in/${url}`;
  };

  // Trigger confetti animation
  const triggerConfetti = () => {
    if (confettiRef.current) {
      try {
        // Create confetti with fallback options for better browser compatibility
        const canvas = confetti.create(confettiRef.current, {
          resize: true,
          useWorker: false, // Disable Web Workers to avoid transferControlToOffscreen issues
        });

        canvas({
          particleCount: 200,
          spread: 160,
          origin: { y: 0.6 },
        });
      } catch (error) {
        console.warn("Confetti animation failed:", error);
        // Fallback to a simpler confetti effect if the main one fails
        try {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
          });
        } catch (e) {
          console.error("Fallback confetti also failed:", e);
        }
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!formData.name || !formData.linkedin || formData.rating === 0) {
      Swal.fire({
        title: "Oops!",
        text: "Veuillez remplir tous les champs obligatoires.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4163eb",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Format LinkedIn URL
      const formattedData = {
        ...formData,
        linkedin: formatLinkedIn(formData.linkedin),
      };

      if (CONTEST_CONFIG.useMockData) {
        // Simulate successful submission for development
        console.log("Using mock submission:", formattedData);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } else {
        console.log("Submitting data to API");

        // Attempt to post to Google Script with proper error handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const apiUrl = `${CONTEST_CONFIG.googleScriptUrl}?action=getStats`;


        // Create FormData object instead of using JSON
        const formDataObj = new FormData();
        formDataObj.append("name", formattedData.name);
        formDataObj.append("linkedin", formattedData.linkedin);
        formDataObj.append("rating", formattedData.rating.toString());
        formDataObj.append("comment", formattedData.comment || "");

        // Use URLSearchParams for x-www-form-urlencoded format
        const params = new URLSearchParams();
        for (const [key, value] of formDataObj.entries()) {
          params.append(key, value.toString());
        }

        const response = await fetch(apiUrl, {
          method: "POST",
          mode: "cors",
          signal: controller.signal,
          body: params, // Send as URLSearchParams
          headers: {
            "Content-Type": "application/x-www-form-urlencoded", // Change content type
          },
        });

        clearTimeout(timeoutId);

        // Handle response differently since it might not be JSON
        if (!response.ok) {
          const errorText = await response.text();
          // Try to parse as JSON, but handle HTML responses
          if (
            errorText.trim().startsWith("<!DOCTYPE") ||
            errorText.trim().startsWith("<html")
          ) {
            throw new Error(
              `API returned HTML error page. Status: ${response.status}`
            );
          }

          try {
            const errorData = JSON.parse(errorText);
            throw new Error(
              errorData.message || `API error: ${response.status}`
            );
          } catch (e) {
            // If it's not valid JSON, just use the text
            throw new Error(
              `API error: ${response.status} - ${errorText.substring(0, 100)}...`
            );
          }
        }

        // Try to parse response as JSON
        let result;
        const responseText = await response.text();
        try {
          result = JSON.parse(responseText);
        } catch (e) {
          console.log("Response is not JSON:", responseText.substring(0, 100));
          // If response is not JSON but status is OK, we'll assume success
          result = { success: true, message: "Submission successful" };
        }

        if (!result.success) {
          throw new Error(result.message || "Unknown error occurred");
        }

        console.log("Submission successful:", result);
      }

      // Show success message
      Swal.fire({
        title: "Merci!",
        text: "Votre participation a été enregistrée avec succès!",
        icon: "success",
        confirmButtonText: "Super !",
        confirmButtonColor: "#4163eb",
      });

      // Trigger confetti
      triggerConfetti();

      // Reset form
      setFormData({
        name: "",
        linkedin: "",
        rating: 0,
        comment: "",
      });

      // Update stats with fresh data
      setTimeout(() => fetchContestStats(), 1000);
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        title: "Erreur!",
        text: `Une erreur est survenue lors de l'envoi du formulaire: ${error.message}`,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#4163eb",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <Helmet>
    <title>Mopaossi | Services</title>
    <meta
      name="description"
      content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
    />
    <meta property="og:title" content="Mopaossi | Développeur Créatif" />
    <meta
      property="og:description"
      content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
    />
    <meta property="og:type" content="website" />
    <link rel="canonical" href="https://cbenji.pages.dev" />
  </Helmet>
      <PageLayout
        title="Mopaossi | Concours"
        description="Participez à notre concours et gagnez un site web personnel !"
      >
        <div className="relative overflow-hidden">
          {/* Confetti container */}
          <div
            ref={confettiRef}
            className="fixed inset-0 pointer-events-none z-50"
          ></div>

          {/* Hero Section */}
          <section className="py-12 md:py-20">
            <div className="container mx-auto px-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-10">
                {/* Left: Text */}
                <div className="w-full md:w-1/2">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <h1 className="text-4xl md:text-5xl font-bold mb-6">
                      <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-500 to-blue-700 animate-gradient">
                        Participe et gagne.</span>
                    </h1>
                    <p className="text-lg text-gray-700 mb-8">
                      Participez à notre concours et tentez de gagner un site web
                      personnel sur mesure. Testez notre plateforme, donnez votre
                      avis, et entrez dans le tirage au sort!
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <Button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md flex items-center gap-2"
                        onClick={() =>
                          document
                            .getElementById("contest-form")
                            ?.scrollIntoView({ behavior: "smooth" })
                        }
                      >
                        Participer <ArrowRight size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        className="border-blue-500 text-blue-500 hover:bg-blue-50 px-6 py-2 rounded-md"
                      >
                        En savoir plus
                      </Button>
                    </div>
                  </motion.div>
                </div>

                {/* Right: Image/Illustration */}
                <motion.div
                  className="w-full md:w-1/2"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="relative">
                    <img
                      src="https://hynmctzoemxttjsmvjyz.supabase.co/storage/v1/object/public/images//jeu.png"
                      alt="Jeu concours"
                      className="h-full w-80 rounded-lg shadow-lg ml-auto"
                    />
                    {/* Decorative elements */}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-12 ">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Total Participants */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <Card
                    ref={section4Ref}
                    className="border-none shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2">
                        <Users className="text-blue-500" />
                        Participants
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-4xl font-bold text-blue-600">
                        {contestStats.totalParticipants}
                      </p>
                      <p className="text-sm text-gray-500">
                        personnes ont déjà participé
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Time Left */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                >
                  <Card
                    ref={section6Ref}
                    className="border-none shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2">
                        <Clock className="text-purple-500" />
                        Temps restant
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-4xl font-bold text-purple-600">
                        {timeLeft}
                      </p>
                      <p className="text-sm text-gray-500">
                        avant la fin du concours
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* High Ratings */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <Card
                    ref={section5Ref}
                    className="border-none shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2">
                        <Award className="text-green-500" />
                        Évaluations positives
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-4xl font-bold text-green-600">
                        {contestStats.highRatings}
                      </p>
                      <p className="text-sm text-gray-500">
                        personnes ont donné 4★ ou plus
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Contest Form Section */}
          <section id="contest-form" className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="text-center mb-10">
                    <h2 className="text-3xl font-bold mb-4">
                      Participez au concours
                    </h2>
                    <p className="text-gray-600">
                      Évaluez notre site web et tentez de gagner un site web
                      personnel sur mesure. Le gagnant sera tiré au sort parmi
                      tous les participants.
                    </p>
                  </div>

                  {isContestActive ? (
                    <Card ref={section7Ref} className="border-none shadow-xl">
                      <CardContent className="pt-6">
                        <form onSubmit={handleSubmit}>
                          <div className="space-y-6">
                            {/* Name Field */}
                            <div>
                              <label
                                htmlFor="name"
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Nom & Prénom *
                              </label>
                              <Input
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Votre nom complet"
                                className="w-full"
                                required
                              />
                            </div>

                            {/* LinkedIn Field */}
                            <div>
                              <label
                                htmlFor="linkedin"
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                LinkedIn *
                              </label>
                              <div className="relative">
                                <Input
                                  id="linkedin"
                                  name="linkedin"
                                  value={formData.linkedin}
                                  onChange={handleInputChange}
                                  placeholder="Votre profil LinkedIn (ex: username ou URL complète)"
                                  className="w-full pl-10"
                                  required
                                />
                                <Linkedin
                                  fill="Blue"
                                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500"
                                  size={18}
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Votre profil LinkedIn nous permet de vous
                                contacter si vous gagnez.
                              </p>
                            </div>

                            {/* Rating Field */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Évaluation *
                              </label>
                              <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    type="button"
                                    onClick={() => handleRatingChange(star)}
                                    className="focus:outline-none"
                                  >
                                    <Star
                                      size={28}
                                      className={`${
                                        formData.rating >= star
                                          ? "text-yellow-400 fill-yellow-400"
                                          : "text-gray-300"
                                      } transition-colors`}
                                    />
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Comment Field */}
                            <div>
                              <label
                                htmlFor="comment"
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Commentaire
                              </label>
                              <Textarea
                                id="comment"
                                name="comment"
                                value={formData.comment}
                                onChange={handleInputChange}
                                placeholder="Partagez votre avis sur notre site web (optionnel)"
                                className="w-full min-h-[100px]"
                              />
                            </div>

                            {/* Submit Button */}
                            <div>
                              <Button
                                type="submit"
                                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-3 rounded-md font-medium"
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Envoi en cours...
                                  </div>
                                ) : (
                                  "Participer au concours"
                                )}
                              </Button>
                            </div>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="border-none shadow-xl bg-gray-50">
                      <CardContent className="pt-6 text-center">
                        <div className="py-10">
                          <h3 className="text-2xl font-bold mb-4 text-gray-700">
                            Concours terminé
                          </h3>
                          <p className="text-gray-600 mb-6">
                            Merci à tous pour vos participations ! Les résultats
                            seront annoncés prochainement.
                          </p>
                          <Button className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md">
                            Être notifié des résultats
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              </div>
            </div>
          </section>

          {/* Recent Participants Section */}
          {contestStats.recentParticipants.length > 0 && (
            <section className="py-12 bg-gray-50">
              <div className="container mx-auto px-4">
                <h2 className="text-2xl font-bold mb-6">
                  Dernières participations
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {contestStats.recentParticipants.map((participant, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{participant.name}</p>
                              <p className="text-sm text-gray-500">
                                {participant.date}
                              </p>
                            </div>
                            <div className="flex">
                              {Array.from({ length: participant.rating }).map(
                                (_, i) => (
                                  <Star
                                    key={i}
                                    size={16}
                                    className="text-yellow-400 fill-yellow-400"
                                  />
                                )
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* FAQ Section */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-3xl font-bold mb-8 text-center">
                  Questions fréquentes
                </h2>
                <div className="space-y-6">
                  <Card
                    ref={section1Ref}
                    className="border-none shadow-md hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <CardTitle>Comment fonctionne les concours sur le site ?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">
                        Participez en remplissant le formulaire d'évaluation.
                        Chaque participation valide vous donne une chance de
                        gagner. Le gagnant sera
                        tiré au sort à la fin de chaque concours.
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    ref={section2Ref}
                    className="border-none shadow-md hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <CardTitle>
                        Quand les résultats seront-ils annoncés ?
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">
                        Les résultats seront annoncés dans les 7 jours suivant la
                        fin du concours. Le gagnant sera contacté directement via
                        LinkedIn.
                      </p>
                    </CardContent>
                  </Card>

                  <Card
                    ref={section3Ref}
                    className="border-none shadow-md hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <CardTitle>Quels sont les prix à gagner?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">
                      De nombreux services digitaux sont à remporter, allant de la création de site web
                      personnalisé jusqu'à d'autres prestations numériques premium.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>
        </div>
    </PageLayout>
    </>
  );
};

export default Services;
